/**
 * UUID Validation Utilities
 *
 * Validates UUIDs before passing to Supabase RPC functions
 * Prevents template string literals and invalid formats
 */

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Validate UUID format
 *
 * @param uuid - UUID string to validate
 * @param label - Optional label for error logging
 * @returns true if valid, false otherwise
 */
export function validateUUID(uuid: string | null | undefined, label = 'UUID'): boolean {
  if (!uuid) {
    console.error(`❌ [UUID VALIDATION] ${label} is empty or undefined`);
    return false;
  }

  // Check for template string literals (common bug)
  if (uuid.includes('<') || uuid.includes('>') || uuid.includes('{') || uuid.includes('}')) {
    console.error(`❌ [UUID VALIDATION] ${label} contains template literal markers:`, uuid);
    return false;
  }

  // Check for placeholder text
  if (uuid.toLowerCase().includes('uuid') || uuid.toLowerCase().includes('id')) {
    if (uuid.length < 30) { // Heuristic: real UUIDs are 36 chars
      console.error(`❌ [UUID VALIDATION] ${label} looks like placeholder text:`, uuid);
      return false;
    }
  }

  // Validate format
  const isValid = UUID_REGEX.test(uuid);

  if (!isValid) {
    console.error(`❌ [UUID VALIDATION] ${label} has invalid format:`, uuid);
    console.error(`   Expected format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx (lowercase hex)`);
  } else {
    console.debug(`✅ [UUID VALIDATION] ${label} is valid:`, uuid.substring(0, 8) + '...');
  }

  return isValid;
}

/**
 * Validate multiple UUIDs at once
 *
 * @param uuids - Object with label-value pairs
 * @returns true if all valid, false if any invalid
 */
export function validateUUIDs(uuids: Record<string, string | null | undefined>): boolean {
  let allValid = true;

  for (const [label, uuid] of Object.entries(uuids)) {
    if (!validateUUID(uuid, label)) {
      allValid = false;
    }
  }

  return allValid;
}

/**
 * Assert UUID is valid (throws error if invalid)
 * Use this when you want to fail fast
 *
 * @param uuid - UUID to validate
 * @param label - Label for error message
 * @throws Error if UUID is invalid
 */
export function assertValidUUID(uuid: string | null | undefined, label = 'UUID'): asserts uuid is string {
  if (!validateUUID(uuid, label)) {
    throw new Error(`Invalid ${label}: ${uuid}`);
  }
}

/**
 * Debug log for bot operations
 * Includes all relevant context for debugging
 */
export function logBotOperation(
  operation: string,
  context: {
    userId?: string | null;
    userEmail?: string | null;
    botId?: string | null;
    botName?: string | null;
    [key: string]: any;
  }
) {
  console.group(`🔧 [BOT OPERATION] ${operation}`);
  console.log('Operation:', operation);
  console.log('Timestamp:', new Date().toISOString());
  console.log('Page:', window.location.href);

  if (context.userId) {
    console.log('User ID:', validateUUID(context.userId, 'User ID') ? '✅ ' + context.userId : '❌ ' + context.userId);
  }
  if (context.userEmail) {
    console.log('User Email:', context.userEmail);
  }
  if (context.botId) {
    console.log('Bot ID:', validateUUID(context.botId, 'Bot ID') ? '✅ ' + context.botId : '❌ ' + context.botId);
  }
  if (context.botName) {
    console.log('Bot Name:', context.botName);
  }

  // Log any additional context
  const { userId, userEmail, botId, botName, ...extra } = context;
  if (Object.keys(extra).length > 0) {
    console.log('Additional Context:', extra);
  }

  console.groupEnd();
}
